import { STRATEGIES } from "./strategies";
import { customRefs, describeRule, rulePositions } from "./rules";
import { runLabCode } from "./pyLab";

/** One list for the backtest: built-in strategies, your coded strategies, and your rule strategies.
 *  Every entry has {id, name, blurb, kind, params}. Positions come from positionsFor() (may run Python). */
export function buildCatalog(items = [], rules = []) {
  const names = Object.fromEntries(items.map((it) => [it.id, { name: it.name, outputs: it.outputs }]));
  const builtin = STRATEGIES.map((st) => ({ ...st, kind: "builtin" }));
  const coded = items
    .filter((it) => it.kind === "strategy")
    .map((it) => ({ id: it.id, name: it.name, blurb: "Your Python strategy.", kind: "code", params: it.params, item: it }));
  const ruled = rules.map((r) => ({ id: r.id, name: r.name, blurb: describeRule(r, names), kind: "rule", params: [], rule: r }));
  return [...builtin, ...coded, ...ruled];
}

/** Coded indicators, for the rule builder's source list. */
export function indicatorItems(items = []) {
  return items.filter((it) => it.kind === "indicator");
}

/** Run every coded indicator a rule needs; returns {"itemId|output": values}. */
async function customValues(rule, candles, items, paramsById) {
  const out = {};
  const refs = customRefs(rule);
  for (const ref of refs) {
    const item = items.find((it) => it.id === ref.itemId);
    if (!item) throw new Error("A coded indicator used by this rule was deleted. Edit the rule.");
    const res = await runLabCode({ code: item.code, candles, paramValues: paramsById[item.id] || {}, packages: item.packages });
    const values = res.outputs[ref.output] || res.outputs[Object.keys(res.outputs)[0]];
    if (!values) throw new Error(`"${item.name}" did not call output(values).`);
    out[`${ref.itemId}|${ref.output || ""}`] = values;
  }
  return out;
}

/** Desired position per bar for any catalog entry. `p` = this entry's params, `paramsById` = everyone's
 *  (rules read their coded indicators' settings from there). */
export async function positionsFor(entry, candles, p, { items = [], paramsById = {} } = {}) {
  if (entry.kind === "builtin") return entry.positions(candles, p);
  if (entry.kind === "code") {
    const res = await runLabCode({ code: entry.item.code, candles, paramValues: p, packages: entry.item.packages });
    if (!res.position) throw new Error("the code never set position = ... (1 long, -1 short, 0 flat per candle).");
    return res.position;
  }
  if (entry.kind === "rule") {
    const custom = await customValues(entry.rule, candles, items, paramsById);
    return rulePositions(candles, entry.rule, custom);
  }
  throw new Error(`Unknown strategy type ${entry.kind}`);
}

/** Does this entry need Python (slow first run)? */
export function needsPython(entry) {
  return entry.kind === "code" || (entry.kind === "rule" && customRefs(entry.rule).length > 0);
}
